import { APPLICATION_ROUTES, catchRenderException, Endpoint, IeAnexo, inISOToDatepicker, inISOToTimestamp, onCatchError, setPredefinedFiles, setRequiredField, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { BaseFileUploaderAPI, BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues, DateFieldAPI, FieldAPI, FieldListValue, FormAPI, RadioGroupItem } from '@philips-emr/tws-components';
import Controller, { OnAfterNavigate, OnBlur, OnChange, OnClose, OnMount, OnSelect, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { AbortableNavigateEvent, ChangeEvent, MountEvent, SelectEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HistoryService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { ExclusaoBeneficiarioAliases as Aliases, FragmentNames } from '../../codegen';
import { Anexo } from '../../interfaces/anexo';
import { EnderecoData } from '../../interfaces/logradouro';

import BaseExclusaoBeneficiarioController, { BeneficiarioSelecionado, IeFormaEnvio, MotivoExclusao, RadioGroupItemData } from './base-exclusao-beneficiario-controller';

import './exclusao-beneficiario-controller.scss';

@Controller({ fragment: FragmentNames.EXCLUSAO_BENEFICIARIO })
export default class ExclusaoBeneficiarioController extends BaseExclusaoBeneficiarioController {

  private regraAnexos: Anexo[];

  @OnMount(Aliases.PANEL_EXCLUSAO_BENEFICIARIO)
  public onMountGetRescisao(): void {
    this.initProperties();
    this.setReenvioSolicitacao();

    const { nrSeqContrato, nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    const { nrSeqRescisao, ieSolicitacaoRescisao } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqRescisao, ieSolicitacaoRescisao });

    if (!nrSeqRescisao || !ieSolicitacaoRescisao) {
      this.hiddeFormCheckbox();
      return;
    }

    this.isEdit = true;
    this.isFormsOnlyReadonly = true;

    void HttpService.get<any>(Endpoint.GET.EXCLUSAO_BENEFICIARIO, { nrSeqRescisao, nrSeqContrato, ieSolicitacaoRescisao, ...(nrSeqSubestipulante && { nrSeqSubestipulante }) })
      .then(({ body: response }) => {
        this.formRescisao.setLoading(false);
        const { nrSeqSolicRescisao, nrSeqSolicRescisaoBenef, nrSeqSegurado, ieSolicitacao, ieStatus } = response;
        this.context.set({ nrSeqSolicRescisao, nrSeqSolicRescisaoBenef, ieSolicitacaoRescisao });

        this.isFormsOnlyReadonly = !(ieSolicitacao === 'S' && ieStatus === 1) && !this.isReenvioSolicitacao;
        this.setWizardReadonly();

        this.beneficiarioExclusao = { nrSeqSolicRescisao, nrSeqSegurado };

        this.validateMotivoRejeicao(ieSolicitacaoRescisao as string, ieStatus);

        const { motivoRescisao, dsObservacao, beneficiarios } = response;

        this.formRescisao.setData({ ...this.formRescisao.getRecord(), motivoRescisao, dsObservacao });

        this.setValueFieldList(beneficiarios);
        this.fieldBeneficiariosLocator.setValue(beneficiarios);
        this.hpmsCustomFieldListBeneficiarios.setReadOnly(true);
        this.locatorBeneficiario.setEnabled(false);
        this.fieldBeneficiariosLocator.setVisible(false);

        void this.shouldHiddenAnexoPromise.then(() => {
          this.initConfigForms(motivoRescisao);
          this.formRescisao.validate();

          const { dtSolicitacao, dtSolicitacaoRescisao, ieFormaEnvio } = response;
          const isIeFormaContatoEmail = ieFormaEnvio === IeFormaEnvio.EMAIL;
          const selectedItemRadio = isIeFormaContatoEmail ? this.emailItemRadio : this.cartaItemRadio;
          let dtSolicitacaoRescisaoInput = null;
          let dtSolicitacaoRescisaoDateField = null;
          this.dtSolicitacaoRescisaoInput = dtSolicitacaoRescisao;

          if (motivoRescisao.ieIniciativaBeneficiario === 'S') {
            this.habilitarCampoRescisaoInput();
            dtSolicitacaoRescisaoInput = inISOToTimestamp(dtSolicitacaoRescisao);
          } else {
            this.habilitarCampoRescisaoDateField();
            dtSolicitacaoRescisaoDateField = inISOToDatepicker(dtSolicitacaoRescisao);
          }

          this.formInfoComplementar.setData({
            ...this.formInfoComplementar.getRecord(),
            dtSolicitacao,
            dtSolicitacaoRescisaoDateField,
            ieFormaEnvio: selectedItemRadio,
          });

          this.formInfoComplementar.validate();

          this.dtRescisaoFieldInput.setValue(dtSolicitacaoRescisaoInput);
          this.dtRescisaoFieldInput.validate();

          const {
            dsEmail,
            telefoneFormatado,
            celularFormatado,
            cdCep,
            dsEndereco,
            nrEndereco,
            dsComplemento,
            dsBairro,
            sgEstado,
            cdMunicipioIbge,
          } = response;

          if (ieSolicitacao === 'S') {

            this.visibilityFieldsContato(isIeFormaContatoEmail, !isIeFormaContatoEmail);

            this.formContato.setData({
              ...this.formContato.getRecord(),
              dsEmail,
              telefoneFormatado,
              celularFormatado,
              cdCep,
              dsEndereco,
              nrEndereco,
              dsComplemento,
              dsBairro,
              sgEstado,
              cdMunicipioIbge,
            });

            this.formContato.validate();

          }

          // form opcional
          catchRenderException(() => {
            const { dtObito, nrCertidaoObito, horaObito } = response;
            this.formObito.setData({ ...this.formObito.getRecord(), dtObito, nrCertidaoObito, horaObito });
          });

          // form opcional
          catchRenderException(() => {
            const { dtConcessao, qtMesesContribuicao } = response;
            const titularContribuiuMensalidadePlano = !!qtMesesContribuicao;
            const aposentouseEmpresa = !!dtConcessao;

            this.tempoContribuicaoInput.setReadOnly(!titularContribuiuMensalidadePlano);
            this.dataConcesaoDateField.setReadOnly(!aposentouseEmpresa);

            this.setValidatorRequiredTempoContribuicao(titularContribuiuMensalidadePlano);
            this.setValidatorRequiredDtConcessaoPlano(aposentouseEmpresa);

            this.formSituacaoTrabalhista.setData({
              ...this.formSituacaoTrabalhista.getRecord(),
              titularContribuiuMensalidadePlano,
              aposentouseEmpresa,
              dtConcessao,
              qtMesesContribuicao,
            });

            this.formSituacaoTrabalhista.validate();
          });

          // form opcional
          catchRenderException(() => {
            const { anexos } = response;

            if (this.isReenvioSolicitacao) {
              if (anexos) {
                this.anexosReenvio = anexos;
                this.mapperAnexosApi.setData(anexos);
              } else {
                this.checkboxReenvioAnexo.setEnabled(false);
              }
            } else if (anexos) {
              this.formAnexo.setData({ ...this.formAnexo.getRecord(), anexos });
              this.mapperAnexosApi.setData(anexos);
            }

            this.anexoField.validate();
            this.formAnexo.validate();
          });

          this.setEditFormsEditFalse(true);
          this.hiddeFormCheckbox();
        });

      }).catch(() => onCatchError());
  }

  @OnMount([
    Aliases.FORM_RESCISAO,
    Aliases.FORM_COMPLEMENTAR,
    Aliases.FORM_OBITO,
    Aliases.FORM_SITUACAO_TRABALHISTA,
    Aliases.FORM_CONTATO,
    Aliases.FORM_ANEXO,
    Aliases.FORM_REENVIO_SOLICITACAO,
  ])
  public onMountForms(event: MountEvent): void {
    const api = event.target as unknown as FormAPI;

    if (api.state.alias === Aliases.FORM_RESCISAO) {
      const { nrSeqRescisao } = this.historyService.getCurrentRouteParams();

      if (!nrSeqRescisao) {
        api.setLoading(false);
      }
    }
    this.exigeCamposObito();
    this.listForms.push(api);
  }

  @OnMount(Aliases.DATE_FIELD_DT_SOLICITACAO)
  public onMountDtSolicitacao(event: MountEvent): void {
    if (this.isEdit) {
      return;
    }

    const dtSolicitacao = event.target as unknown as DateFieldAPI;
    dtSolicitacao.setValue(this.getDateFromISO(new Date().toISOString()));
  }

  @OnSubmit(Aliases.WIZARD_NOVA_EXCLUSAO)
  public saveNovaExclusao(): void {
    if (this.isFormsOnlyReadonly) {
      return;
    }

    const body: any = this.listForms.map((form) => form.getRecord())
      .reduce((accumulator, value) => accumulator = { ...accumulator, ...value });

    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    body.ieFormaEnvio = body.ieFormaEnvio?.value;
    body.beneficiarios = this.hpmsCustomFieldListBeneficiarios.getValue();
    body.dtSolicitacaoRescisao = this.getDtRescisaoFmtISO(body.motivoRescisao);

    body.ieSolicitacao = this.historyService.getCurrentRouteParams().ieSolicitacaoRescisao;

    if (body.anexos) {
      body.anexos = this.handleAnexos(body.anexos as Anexo[]);
    }

    if (this.isReenvioSolicitacao) {
      body.nrSeqSolicRescisaoRejeitada = this.beneficiarioExclusao?.nrSeqSolicRescisao;
      body.nrSeqSegurado = this.beneficiarioExclusao?.nrSeqSegurado;
      catchRenderException(() => body.isAnexosCopiado = !!this.formReenvioSolicitacao.getRecord().reenviarAnexos);
    } else {
      body.nrSeqSolicRescisao = this.beneficiarioExclusao?.nrSeqSolicRescisao;
      body.nrSeqSegurado = this.beneficiarioExclusao?.nrSeqSegurado;
    }

    delete body.dtSolicitacaoRescisaoDateField;
    delete body.dtSolicitacaoRescisaoInput;
    delete body.beneficiarios_locator;

    const params = { nrSeqContrato, ...(this.isEdit && { nrSeqRescisao: this.beneficiarioExclusao?.nrSeqSolicRescisao }) };

    this.setWizardReadonly(true);

    this.saveRescisao(params, body).then(({ body }) => {
      const result = body?.response;

      result && this.fetchAnexos(nrSeqContrato, result.nrSeqSolicRescisao, result.nrSeqSegurado);
      this.setEditFormsEditFalse();
      const { motivoRescisao } = this.formRescisao.getRecord();
      const { nrSeqGrupoContratoWeb } = HistoryService.getCurrentRouteParams();
      HistoryService.push(APPLICATION_ROUTES.EXCLUSAO_BENEFICIARIO, { nrSeqContrato, nrSeqGrupoContratoWeb });
      ModalService.openDialog({
        title: 'stipulator.solicitacao_excluxao_gravada',
        fragment: FragmentNames.SOLICITACAO_EXCLUSAO_FEEDBACK,
        contextData: {
          dsMensagemWeb: (motivoRescisao as unknown as MotivoExclusao)?.dsMensagemWeb,
          protocolos: result?.protocolos,
          ieLiberarRescisao: result?.ieLiberarRescisao,
        },
      });
    }).catch(({ body }) => {
      this.setWizardReadonly(false);

      const response = body?.response;

      if (response?.showDataLimiteNotification) {
        this.wizardNovaExclusao.editStep(this.identifierStepComplementar);
        this.fieldToast.setVisible(true);
        this.toastFormDataLimite.setVisible(true);
        this.toastFormDataLimite.setMessage(MessageService.translate('stipulator.dt-rescisao-posterior').replace('#@DT_LIMITE#@', response.dataLimite));
        return;
      }

      if (!isEmptyOrBlankString(response?.message)) {
        onCatchError(response?.message);
      } else if (!isEmptyOrBlankString(response)) {
        onCatchError(response);
      }

      onCatchError();
    });
  }

  @OnMount(Aliases.FIELD_LIST_BENEFICIARIOS)
  public onMountListBeneficiarios(): void {
    this.setValueFieldList([]);
  }

  @OnMount([
    Aliases.FIELD_EMAIL,
    Aliases.FIELD_CEP,
    Aliases.FIELD_ENDERECO,
    Aliases.FIELD_NUMERO,
    Aliases.FIELD_COMPLEMENTO,
    Aliases.FIELD_BAIRRO,
    Aliases.FIELD_ESTADO,
    Aliases.FIELD_CIDADE,
    Aliases.FIELD_TELEFONE,
    Aliases.FIELD_CELULAR,
  ])
  public onMountFormContatoRequired(event: MountEvent): void {
    this.listFieldContatos.push(event.target as unknown as FieldAPI);
  }

  @OnMount(Aliases.FIELD_TITULAR)
  public onMountFieldTitular(event: MountEvent): void {
    const field = event.target as unknown as FieldAPI;
    const value = field.getData() as unknown as BeneficiarioSelecionado;
    value && value.nrSeqSegurado && field.setLabel(value.dsTitularidade);
  }

  @OnChange(Aliases.FIELD_LIST_BENEFICIARIOS)
  public onChangeListBeneficiarios(changeEvent: ChangeEvent<FieldListValue>): void {
    this.isEditStepRescisao();
    this.setValueFieldList(changeEvent.value);
  }

  @OnChange(Aliases.LOCATOR_BENEFICIARIOS_EXCLUSAO)
  public onChangeLocatorBeneficiarioExclusao(changeEvent: ChangeEvent<FieldListValue>): void {
    this.isEditStepRescisao();

    const beneficiarios = changeEvent.value;
    let value = this.hpmsCustomFieldListBeneficiarios.getValue();

    if (value.length == 1 && Object.keys(value[0]).length === 0) {
      value = [];
    }

    if (beneficiarios.length) {
      this.setValueFieldList(value.concat(beneficiarios));
    } else {
      this.setValueFieldList(beneficiarios);
    }

  }

  @OnChange(Aliases.FIELD_FORMA_CONTATO)
  public onChangeFieldFormaContato(changeEvent: ChangeEvent<RadioGroupItem>): void {
    this.wizardNovaExclusao.editStep(this.identifierStepComplementar);
    const itemRadio = changeEvent.value;
    this.setDataFormContato(itemRadio);
    this.setIeFormaEnvioInForm(itemRadio);
  }

  @OnChange(Aliases.FIELD_MENSALIDADE_PLANO)
  public onChangeFieldMensalidadePlano(changeEvent: ChangeEvent<boolean>): void {
    const isReadOnly = !changeEvent.value;

    if (isReadOnly) {
      this.qtTempoContribuicaoField.setValue('');
    }

    this.tempoContribuicaoInput.setReadOnly(isReadOnly);
    this.setValidatorRequiredTempoContribuicao(!isReadOnly);
  }

  @OnChange(Aliases.FIELD_APOSENTOU_EMPRESA)
  public onChangeFieldAposentouseEmpresa(changeEvent: ChangeEvent<boolean>): void {
    const isReadOnly = !changeEvent.value;

    if (isReadOnly) {
      this.dtConcessaoField.setValue(null);
    }

    this.dataConcesaoDateField.setReadOnly(isReadOnly);
    this.setValidatorRequiredDtConcessaoPlano(!isReadOnly);
  }

  @OnChange(Aliases.FIELD_REENVIO_ANEXO)
  public onChangeFieldReenvioAnexo(event: ChangeEvent<boolean>): void {
    const anexos = event.value ? this.anexosReenvio : [];

    this.anexoField.setValue(anexos);
    this.anexoField.validate();
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  public onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { value } = event;
    const { filename, nrSeqAnexo } = value;
    const { nrSeqContrato, nrSeqRescisao } = this.historyService.getCurrentRouteParams();

    let ieTabela: IeAnexo;

    const { ieSolicitacaoRescisao } = this.historyService.getCurrentRouteParams();

    if (ieSolicitacaoRescisao === 'S' || !this.isEdit) {
      ieTabela = IeAnexo.SOLICITACAO_RESC_ANEXO;
    } else {
      ieTabela = IeAnexo.SEGURADO_DOC_ARQ;
    }

    FileService.download(Endpoint.GET.EXCLUSAO_BENEFICIARIO_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato, nrSeqRescisao }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));

  }

  @OnChange(Aliases.FIELD_MOTIVO_EXCLUSAO)
  public onChangeFieldMotivoExclusao(changeEvent: ChangeEvent<MotivoExclusao>): void {
    const motivoRescisao = changeEvent.value;
    this.initConfigForms(motivoRescisao);

    this.isEdit && this.swapFieldsFmtDtRescisao(motivoRescisao);
  }

  @OnMount(Aliases.FILE_UPLOADER_ANEXO)
  public onMountFileUploaderAnexo(event: MountEvent): void {
    const api = event.target as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;

    void this.shouldHiddenAnexoPromise.then(() =>
      setPredefinedFiles(this.ieFuncionalidade, api, [])
        .then(regraAnexos => this.regraAnexos = regraAnexos as unknown as Anexo[])
    );
  }

  @OnChange(Aliases.UNBOXED_AUTOCOMPLETE_ESTADO)
  public onChangeFieldEstado(e: ChangeEvent<string>): void {
    this.fetchCidade.setParameters({ dsUnidadeFederacao: e.value });
  }

  @OnValidate(Aliases.FORM_RESCISAO)
  public onValidateFormRescisao(event: ValidateEvent): void {
    this.setStepDone(this.identifierStepRescisao, event);
  }

  @OnValidate(Aliases.FORM_COMPLEMENTAR)
  public onValidateFormComplementar(event: ValidateEvent): void {
    this.setStepDone(this.identifierStepComplementar, event);
  }

  @OnValidate(Aliases.FORM_CONTATO)
  public onValidateFormContato(event: ValidateEvent): void {
    this.setStepDone(this.identifierStepContato, event);
  }

  @OnValidate(Aliases.FORM_OBITO)
  public onValidateFormObito(event: ValidateEvent): void {
    this.setStepDone(this.identifierStepObito, event);
  }

  @OnValidate(Aliases.FORM_SITUACAO_TRABALHISTA)
  public onValidateFormSituacaoTrabalhista(event: ValidateEvent): void {
    this.setStepDone(this.identifierStepSituacaoTrabalhista, event);
  }

  @OnValidate(Aliases.FORM_ANEXO)
  public onValidateFormAnexos(event: ValidateEvent): void {
    this.setStepDone(this.identifierStepAnexo, event);
  }

  @OnAfterNavigate(Aliases.WIZARD_NOVA_EXCLUSAO)
  public onNavigateWizardNovaExclusao(event: AbortableNavigateEvent<string>): void {
    if (!event || !event.to) {
      return;
    }

    if (event.from === this.identifierStepRescisao) {
      this.fieldBeneficiariosLocator.setVisible(false);
    }

    if (this.identifierStepRescisao === event.to) {
      if (!this.fieldBeneficiariosLocator.isVisible()) {
        this.fieldBeneficiariosLocator.setVisible(true);
      }
    } else if (this.identifierStepContato === event.to) {
      this.validateFormContato();
      this.formContato.validate();
    } else if (this.identifierStepComplementar === event.to) {
      const motivoRescisao = this.formRescisao.getRecord().motivoRescisao as unknown as MotivoExclusao;
      const checkEdicao = (!this.isEdit || this.isReenvioSolicitacao);

      if (checkEdicao && motivoRescisao?.ieIniciativaBeneficiario === 'S') {
        if (motivoRescisao.ieSuspensaoAtendimento === '1') {
          this.habilitarCampoRescisaoInput();
          const dtSolicitacaoRescisaoInput = inISOToTimestamp(new Date());

          this.formInfoComplementar.setRecord({ ...this.formInfoComplementar.getRecord(), dtSolicitacaoRescisaoInput });
          this.formInfoComplementar.setData({ ...this.formInfoComplementar.getRecord(), dtSolicitacaoRescisaoInput });
          this.dtRescisaoFieldInput.setValue(dtSolicitacaoRescisaoInput);

          this.formInfoComplementar.validate();
          this.dtRescisaoFieldInput.validate();
        } else if (motivoRescisao.ieSuspensaoAtendimento === '2') {
          this.habilitarCampoRescisaoInput();
          const date = new Date();
          date.setHours(23);
          date.setMinutes(59);
          date.setSeconds(59);

          const dtSolicitacaoRescisaoInput = inISOToTimestamp(date);

          this.formInfoComplementar.setRecord({ ...this.formInfoComplementar.getRecord(), dtSolicitacaoRescisaoInput });
          this.formInfoComplementar.setData({ ...this.formInfoComplementar.getRecord(), dtSolicitacaoRescisaoInput });
          this.dtRescisaoFieldInput.setValue(dtSolicitacaoRescisaoInput);

          this.formInfoComplementar.validate();
          this.dtRescisaoFieldInput.validate();
        } else {
          this.habilitarCampoRescisaoDateField();

          this.formInfoComplementar.setData({
            ...this.formInfoComplementar.getRecord(),
            dtSolicitacaoRescisaoDateField: null,
          });

          this.formInfoComplementar.validate();
          this.dtRescisaoFieldDateField.validate();
        }
        this.setDadosFormComplementarUnicoItemRadio();
      } else if (this.isEdit) {

        this.setDadosFormComplementarUnicoItemRadio();
        this.dtRescisaoFieldInput.validate();
        this.dtRescisaoFieldDateField.validate();
      }

      this.formInfoComplementar.validate();
    }

    this.listForms.forEach((form) => form.setEditing(false));
    this.updateFormEditingNavigation(event.to, true);
    this.hiddeFormCheckbox();
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  public onCloseToast(): void {
    this.toastFormDataLimite.setVisible(false);
  }

  @OnBlur(Aliases.FIELD_CEP)
  public onBlurCdCep(event: FocusEvent): void {
    const api = event.target as unknown as FieldAPI;

    if (api.isValid() && api.getValue()) {
      void HttpService.get<EnderecoData>(Endpoint.GET.ENDERECO_CEP, { cdCep: api.getValue() })
        .then(({ body }) => this.setEndereco(body));
    }
  }

  @OnMount(Aliases.FIELD_DT_OBITO)
  onMountDataObito(event: MountEvent): void {
    const api = event.target as FieldAPI;

    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    void HttpService.get(Endpoint.GET.EXIGE_CAMPOS_OBITO, { nrSeqContrato }).then(({ body: response }) => {

      switch (response) {
        case 1:
          api.setValidators([{ name: 'required', options: {} }] as Validator[]);
          break;
        case 2:
          api.setValidators([{ name: 'required', options: {} }] as Validator[]);
          break;
      }

    });
  }

  @OnMount(Aliases.FIELD_CERTIDAO_OBITO)
  onMountCertidaoObito(event: MountEvent): void {
    const api = event.target as FieldAPI;

    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    void HttpService.get(Endpoint.GET.EXIGE_CAMPOS_OBITO, { nrSeqContrato }).then(({ body: response }) => {

      if (response && response == '2') {
        api.setValidators([{ name: 'required', options: {} }] as Validator[]);
      }

    });
  }

  @OnMount(Aliases.FIELD_FORMA_CONTATO)
  onMountFormaContato(event: MountEvent): void {
    const { ieSolicitacaoRescisao } = this.historyService.getCurrentRouteParams();
    const api = event.target as FieldAPI;

    if (ieSolicitacaoRescisao == 'N') {
      api.setVisible(false);

      catchRenderException(() => {
        setRequiredField(false, api);
      });

    }

  }

  private setEndereco(result: EnderecoData | undefined): void {
    if (!result) {
      return;
    }

    const { dsUf } = result;

    this.formContato.setData({
      ...this.formContato.getRecord(),
      ...result,
      sgEstado: dsUf,
    });

    if (dsUf) {
      this.fetchCidade.setParameters({ dsUnidadeFederacao: dsUf });
      this.fetchCidade.forceFetch();
    }
  }

  private setDadosFormComplementarUnicoItemRadio(): void {
    const { nrSeqContrato, nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    const { nrSeqRescisao, ieSolicitacaoRescisao } = this.historyService.getCurrentRouteParams();

    void HttpService.get<any>(Endpoint.GET.EXCLUSAO_BENEFICIARIO, { nrSeqRescisao, nrSeqContrato, ieSolicitacaoRescisao, ...(nrSeqSubestipulante && { nrSeqSubestipulante }) })
      .then(({ body: response }) => {
        const { dtSolicitacao, dtSolicitacaoRescisao } = response;
        const dtSolicitacaoRescisaoDateField = inISOToDatepicker(dtSolicitacaoRescisao);

        const radioGroup = this.modoContato.getData();
        if (radioGroup.length === 1) {
          const { value } = radioGroup[0] as RadioGroupItemData;

          if (value === '2') {
            this.formInfoComplementar.setData({
              ...this.formInfoComplementar.getRecord(),
              dtSolicitacao,
              dtSolicitacaoRescisaoDateField,
              ieFormaEnvio: this.cartaItemRadio,
            });
            this.setDataFormContato(this.cartaItemRadio);
          } else if (value === '1') {
            this.formInfoComplementar.setData({
              ...this.formInfoComplementar.getRecord(),
              dtSolicitacao,
              dtSolicitacaoRescisaoDateField,
              ieFormaEnvio: this.emailItemRadio,
            });
            this.setDataFormContato(this.emailItemRadio);

          }

        }
      });
  }

  protected exigeCamposObito(): void {
    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    void HttpService.get(Endpoint.GET.EXIGE_CAMPOS_OBITO, { nrSeqContrato }).then(({ body: response }) => {
      catchRenderException(() => {
        switch (response) {
          case 1:
            this.dtObitoField.setValidators([{ name: 'required', options: {} }] as Validator[]);
            this.certidaoObito.setValidators([{ name: 'required', options: {} }] as Validator[]);
            break;
          case 2:
            this.dtObitoField.setValidators([{ name: 'required', options: {} }] as Validator[]);
            break;
        }
      });
    });

  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

}
